import { DownloadSimple, Eye, File, Trash } from '@phosphor-icons/react';
import { Button, cn, FileDropzone, Modal, Spinner } from '@skupreme/shared/ui';
import { useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { pdfjs } from 'react-pdf';
import useUrlPermission from '@hooks/url-permission';
import PermissionTooltip from '@components/PermissionTooltip';
import { PreviewImage, PreviewPDF } from './PreviewImage';

/* Set here, once, for every react-pdf viewer in the app, including PreviewPDF. */
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type UploadedFile = {
  key?: string;
  file_name?: string;
  file_type?: string;
  filePath?: string;
};

type HoverSmallFileProps = {
  file: UploadedFile;
  removeDocument?: () => void;
  actionAllowed?: boolean;
  /** Layout classes from the caller, e.g. `col-2` or `w-100`. */
  colClass?: string;
  dummyText?: ReactNode;
  selectedKey?: string;
  /** The dark variant, for files listed inside a dark popup. */
  noHover?: boolean;
  fullWidth?: boolean;
};

/** An uploaded file's chip: opens the file on click, with view/download and delete on hover. */
export function HoverSmallFile({
  file,
  removeDocument,
  actionAllowed,
  colClass = 'sk:w-2/12 sk:flex-none sk:px-4',
  dummyText,
  selectedKey,
  noHover,
  fullWidth,
}: HoverSmallFileProps) {
  const { disabled, hidden } = useUrlPermission();
  const isPreviewable = useMemo(
    () =>
      file.file_type?.split('/')[0] === 'image' ||
      Boolean(file.file_type?.includes('pdf')),
    [file.file_type],
  );

  const openFile = (event: MouseEvent) => {
    event.stopPropagation();
    window.open(file.filePath, '_blank', 'noreferrer');
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the View and Download buttons inside are the keyboard path
    <div
      className={cn(
        colClass,
        'sk:my-1! sk:p-2! hover-div',
        'sk:group sk:relative sk:flex sk:h-[38px] sk:min-w-48 sk:cursor-pointer sk:items-center sk:rounded-[5px] sk:border sk:border-solid',
        selectedKey !== undefined && selectedKey === file.key
          ? 'sk:border-success'
          : 'sk:border-border',
        noHover ? 'sk:hover:text-white' : 'sk:hover:text-fg',
        fullWidth && 'sk:w-max sk:max-w-max',
      )}
      onClick={openFile}
    >
      <File className="sk:min-w-[18px]" size={18} aria-hidden />
      <span className="upload-file-name sk:mx-0.5 sk:h-[18px] sk:truncate sk:text-[13px]">
        {dummyText || file.file_name}
      </span>
      <div
        className={cn(
          /* Faded rather than `display: none`, so Tab can still reach the buttons and reveal them. */
          'sk:pointer-events-none sk:absolute sk:right-2 sk:flex sk:items-center sk:gap-1 sk:opacity-0',
          'sk:group-hover:pointer-events-auto sk:group-hover:opacity-100 sk:focus-within:pointer-events-auto sk:focus-within:opacity-100',
          /* Without hover, as on touch-only screens, the actions stay visible. */
          'sk:[@media(hover:none)]:pointer-events-auto sk:[@media(hover:none)]:opacity-100',
          noHover ? 'sk:bg-black sk:text-white' : 'sk:bg-surface sk:text-fg',
        )}
      >
        {actionAllowed && (
          <PermissionTooltip
            disabled={disabled}
            hidden={hidden}
            position="bottom center"
            wrapperClassName={`sk:flex! ${disabled ? 'sk:cursor-not-allowed sk:opacity-70 sk:[&_*]:cursor-not-allowed' : ''}`}
          >
            <button
              type="button"
              aria-label={`Delete ${file.file_name ?? 'file'}`}
              className="sk:ml-1! sk:inline-flex sk:cursor-pointer sk:border-0 sk:bg-transparent sk:p-0 sk:text-inherit"
              onClick={event => {
                event.stopPropagation();
                if (!disabled) removeDocument?.();
              }}
            >
              <Trash size={18} aria-hidden />
            </button>
          </PermissionTooltip>
        )}
        {isPreviewable ? (
          <button
            type="button"
            aria-label={`View ${file.file_name ?? 'file'}`}
            className="sk:flex sk:cursor-pointer sk:items-center sk:border-0 sk:bg-transparent sk:p-0 sk:text-inherit"
            onClick={openFile}
          >
            <Eye size={18} className="sk:mr-1!" aria-hidden />
            {!actionAllowed && <span aria-hidden>View</span>}
          </button>
        ) : (
          <a
            href={file.filePath}
            target="_blank"
            rel="noreferrer"
            aria-label={`Download ${file.file_name ?? 'file'}`}
            className="sk:ml-1! sk:flex sk:items-center sk:text-inherit"
            onClick={event => event.stopPropagation()}
          >
            <DownloadSimple size={18} />
          </a>
        )}
      </div>
    </div>
  );
}

const BYTES_PER_MB = 1048576;

/** A picked file as the callers store it. */
export type PickedFile = { name: string; file: File };

type PreviewKind = 'image' | 'pdf';

/* The callers pass react-dropzone's comma-separated `accept` string. */
function acceptList(accept?: string): string[] | undefined {
  const entries = accept
    ?.split(',')
    .map(entry => entry.trim())
    .filter(Boolean);
  return entries?.length ? entries : undefined;
}

function previewKindOf(file: File): PreviewKind | null {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  return file.type.includes('pdf') ? 'pdf' : null;
}

/** A Preview button for image and PDF rows, and the viewer it opens. */
function useFilePreview() {
  const [preview, setPreview] = useState<{ url: string; kind: PreviewKind }>();

  const close = () => {
    if (preview) {
      URL.revokeObjectURL(preview.url);
    }
    setPreview(undefined);
  };

  const renderAction = (file: File) => {
    const kind = previewKindOf(file);
    if (!kind) {
      return null;
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        shape="circle"
        aria-label={`Preview ${file.name}`}
        onPress={() => setPreview({ url: URL.createObjectURL(file), kind })}
      >
        <Eye size={14} aria-hidden />
      </Button>
    );
  };

  const viewer = (
    <>
      <PreviewImage
        open={preview?.kind === 'image'}
        url={preview?.url ?? ''}
        closeModal={close}
      />
      <PreviewPDF
        open={preview?.kind === 'pdf'}
        url={preview?.url ?? ''}
        closeModal={close}
      />
    </>
  );

  return { renderAction, viewer };
}

type Instruction = { id: string | number; label: ReactNode };

type FileUploadProps = {
  open: boolean;
  title?: ReactNode;
  closeModal: () => void;
  /** react-dropzone's comma-separated list, e.g. `".csv, application/vnd.ms-excel"`. */
  accept?: string;
  maxFiles?: number;
  /** In megabytes. */
  maxFileSize?: number;
  uploading?: boolean;
  upload: (files: File[]) => void;
  subTitle?: ReactNode;
  instructions?: Instruction[];
  bodyElement?: ReactNode;
};

/** A dialog that picks files and hands them to `upload` on Submit. */
export function FileUpload({
  open,
  title,
  closeModal,
  accept,
  maxFiles,
  maxFileSize,
  uploading = false,
  upload,
  subTitle,
  instructions = [],
  bodyElement,
}: FileUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { renderAction, viewer } = useFilePreview();

  const close = () => {
    setFiles([]);
    closeModal();
  };

  const submit = () => {
    upload(files);
    setFiles([]);
  };

  return (
    <>
      <Modal
        isOpen={open}
        onOpenChange={isOpen => {
          if (!isOpen) {
            close();
          }
        }}
        title={title}
        size="md"
        isDismissable={!uploading}
        footer={
          <>
            <Button variant="pill" size="md" onPress={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              isDisabled={files.length === 0 || uploading}
              onPress={submit}
            >
              Submit
            </Button>
          </>
        }
      >
        <div className="sk:flex sk:flex-col sk:gap-4">
          {subTitle ? (
            <p className="sk:m-0 sk:text-dense sk:text-fg-secondary">
              {subTitle}
            </p>
          ) : null}
          {uploading ? (
            <span className="sk:inline-flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
              <Spinner size="sm" label="Uploading files" />
              <span aria-hidden>Uploading files</span>
            </span>
          ) : null}
          {instructions.length > 0 ? (
            <ol className="sk:m-0 sk:flex sk:list-none sk:flex-col sk:gap-2 sk:p-0 sk:text-dense sk:text-fg">
              {instructions.map(({ id, label }) => (
                <li key={id} className="sk:flex sk:items-center sk:gap-2">
                  {instructions.length > 1 ? (
                    <span className="sk:text-fg-tertiary">{id}</span>
                  ) : null}
                  {label}
                </li>
              ))}
            </ol>
          ) : null}
          <FileDropzone
            value={files}
            onChange={setFiles}
            accept={acceptList(accept)}
            multiple={!maxFiles || maxFiles > 1}
            maxFiles={maxFiles && maxFiles > 1 ? maxFiles : undefined}
            maxSize={maxFileSize ? maxFileSize * BYTES_PER_MB : undefined}
            isDisabled={uploading}
            renderFileActions={renderAction}
          />
          {bodyElement}
        </div>
      </Modal>
      {viewer}
    </>
  );
}

type FileUploadDragAndDropProps = {
  accept?: string;
  maxFiles?: number;
  /** In megabytes. */
  maxFileSize?: number;
  files: PickedFile[];
  setFiles: (files: PickedFile[]) => void;
  /** Sized the old thumbnails; the file list has none, so it is unused. */
  previewImageContainerClass?: string;
};

/** The same picker inline, for forms that submit the files themselves. */
export function FileUploadDragAndDrop({
  accept,
  maxFiles,
  maxFileSize,
  files,
  setFiles,
}: FileUploadDragAndDropProps) {
  const { renderAction, viewer } = useFilePreview();

  return (
    <>
      <FileDropzone
        value={files.map(picked => picked.file)}
        onChange={next =>
          setFiles(
            next.map(
              file =>
                files.find(picked => picked.file === file) ?? {
                  name: file.name,
                  file,
                },
            ),
          )
        }
        accept={acceptList(accept)}
        multiple={!maxFiles || maxFiles > 1}
        maxFiles={maxFiles && maxFiles > 1 ? maxFiles : undefined}
        maxSize={maxFileSize ? maxFileSize * BYTES_PER_MB : undefined}
        renderFileActions={renderAction}
      />
      {viewer}
    </>
  );
}
